// Estado compartido por las piezas de una sesión: cada slot se monta suelto y el PUT es uno solo.
import { create } from 'zustand';
import type { LinePromotionDiscount } from '../api/wizardPromotionsContract';
import {
    seedFromSession,
    type SelectionChange,
    type SelectionPort,
    type SessionPromotionContext,
} from '../lib/applySelection';
import { nextDiscountCodeBlock, type DiscountCodeBlockCode } from '../lib/discountCodeBlock';
import {
    EMPTY_SELECTION,
    rebaseOnAnchor,
    type DroppedCode,
    type PromotionSelection,
} from '../lib/promotionSelection';
import type { PhoneDraft } from '../lib/phoneDraft';
import { clearStoredDraft, readStoredDraft, writeStoredDraft } from '../lib/draftStorage';
import type { SureKeepReleased } from '../lib/sureKeepRelease';

/** Código que Promociones rechazó al continuar en «Elige el teléfono», con su motivo. */
export interface PhoneCodeRejection {
    code: string;
    message: string;
}

export interface SessionPromotionState {
    selection: PromotionSelection;
    /** Precio por línea que devolvió el último apartado. */
    lines: LinePromotionDiscount[];
    /** Code del servidor que bloqueó el código de descuento; con él se pliega el campo. */
    discountCodeBlockedBy: DiscountCodeBlockCode | null;
    /** La selección refleja lo que tiene la sesión; mientras no, nada escribe en automático. */
    synced: boolean;
    /** Código que se quitó al reaplicar, para avisarle al vendedor. */
    droppedCode: DroppedCode | null;
    /** Última ancla con la que se aplicó; al cambiar, se vuelve a aplicar (REQ-13). */
    saleKey?: string;
    /** Error del último reaplicar disparado por el wizard (extras o forma de pago). */
    reapplyError: unknown;
    /** Sube cada vez que el wizard avisa que cambiaron las líneas de la venta. */
    linesVersion: number;
    /** El vendedor eligió «Ninguna»: ya no se preselecciona promo de venta en la sesión. */
    saleDeclined?: boolean;
    /** Cambios del vendedor que aún no confirma el PUT; se muestran ya aplicados. */
    pendingChanges?: SelectionChange[];
    /** Lo solo SureKeep que se soltó al pasar a contado; se reaparta al volver a SureKeep. */
    sureKeepReleased?: SureKeepReleased;
    /** Códigos que siguieron aplicando de contado: no son solo SureKeep. */
    anyFinancierCodes?: string[];
    /** Lo que el vendedor marcó en «Elige el teléfono»; se aparta al continuar. */
    phoneDraft?: PhoneDraft;
    /** Código rechazado al continuar en «Elige el teléfono». */
    phoneCodeRejection?: PhoneCodeRejection | null;
}

export const EMPTY_SESSION_STATE: SessionPromotionState = {
    selection: EMPTY_SELECTION,
    lines: [],
    discountCodeBlockedBy: null,
    synced: false,
    droppedCode: null,
    reapplyError: null,
    linesVersion: 0,
};

const hydrated = new Map<string, SessionPromotionState>();

/** Estado de arranque de una sesión: vacío más lo marcado que sobrevivió a un F5. */
function initialState(sessionId: string): SessionPromotionState {
    const cached = hydrated.get(sessionId);
    if (cached) return cached;
    const stored = readStoredDraft(sessionId);
    const state =
        stored.phoneDraft === undefined && stored.saleDeclined === undefined
            ? EMPTY_SESSION_STATE
            : { ...EMPTY_SESSION_STATE, ...stored };
    hydrated.set(sessionId, state);
    return state;
}

interface PromotionSelectionStore {
    sessions: Record<string, SessionPromotionState | undefined>;
    patch: (sessionId: string, patch: Partial<SessionPromotionState>) => void;
    reset: (sessionId: string) => void;
}

export const usePromotionSelectionStore = create<PromotionSelectionStore>((set) => ({
    sessions: {},
    patch: (sessionId, patch) => {
        set((state) => {
            const next = { ...(state.sessions[sessionId] ?? initialState(sessionId)), ...patch };
            if ('phoneDraft' in patch || 'saleDeclined' in patch) {
                writeStoredDraft(sessionId, {
                    ...(next.phoneDraft !== undefined && { phoneDraft: next.phoneDraft }),
                    ...(next.saleDeclined !== undefined && { saleDeclined: next.saleDeclined }),
                });
            }
            return { sessions: { ...state.sessions, [sessionId]: next } };
        });
    },
    reset: (sessionId) => {
        hydrated.delete(sessionId);
        clearStoredDraft(sessionId);
        set((state) => ({ sessions: { ...state.sessions, [sessionId]: undefined } }));
    },
}));

export function sessionPromotionState(sessionId: string): SessionPromotionState {
    return usePromotionSelectionStore.getState().sessions[sessionId] ?? initialState(sessionId);
}

export function useSessionPromotionState(sessionId: string): SessionPromotionState {
    return usePromotionSelectionStore((s) => s.sessions[sessionId]) ?? initialState(sessionId);
}

/** La selección con los cambios en camino ya aplicados: el renglón se marca al instante. */
export function usePreviewSelection(sessionId: string): PromotionSelection {
    const { selection, pendingChanges = [] } = useSessionPromotionState(sessionId);
    return pendingChanges.reduce((next, change) => change(next), selection);
}

/** Anota o quita un cambio en camino. */
export function trackPendingChange(sessionId: string, change: SelectionChange, on: boolean): void {
    const current = sessionPromotionState(sessionId).pendingChanges ?? [];
    const pendingChanges = on ? [...current, change] : current.filter((c) => c !== change);
    usePromotionSelectionStore.getState().patch(sessionId, { pendingChanges });
}

/** Si el error bloquea el código de descuento, guarda el code que lo bloqueó. */
export function recordDiscountCodeBlock(sessionId: string, error: unknown): void {
    const current = sessionPromotionState(sessionId).discountCodeBlockedBy;
    const next = nextDiscountCodeBlock(current, error);
    if (next !== current) {
        usePromotionSelectionStore.getState().patch(sessionId, { discountCodeBlockedBy: next });
    }
}

/** La fila de escrituras lee y guarda la selección de la sesión en este store. */
export function storePort(sessionId: string): SelectionPort {
    return {
        read: () => {
            const state = sessionPromotionState(sessionId);
            return state.synced ? state.selection : null;
        },
        commit: ({ selection: held, lines, droppedCode }) => {
            // Si la ancla cambió mientras el PUT iba en camino, lo apartado se pasa a la nueva.
            const anchor = sessionPromotionState(sessionId).selection.anchorLineId;
            const selection =
                anchor !== null && held.anchorLineId !== null && held.anchorLineId !== anchor
                    ? rebaseOnAnchor(held, anchor, Object.keys(held.byLine), EMPTY_SELECTION)
                    : held;
            usePromotionSelectionStore
                .getState()
                .patch(sessionId, { selection, lines, droppedCode, synced: true });
        },
    };
}

/** Sin memoria (recarga, otra pestaña) toma del contexto lo que ya tiene la sesión. */
export function seedSessionPromotions(sessionId: string, context: SessionPromotionContext): void {
    if (sessionPromotionState(sessionId).synced) return;
    usePromotionSelectionStore
        .getState()
        .patch(sessionId, { selection: seedFromSession(context), synced: true });
}

/** El wizard avisó que cambiaron las líneas: lo que dependía de las anteriores se descarta. */
export function bumpLinesVersion(sessionId: string): void {
    const { linesVersion } = sessionPromotionState(sessionId);
    usePromotionSelectionStore.getState().patch(sessionId, { linesVersion: linesVersion + 1 });
}

/** El código solo aplica con SureKeep; sin saberlo se asume que sí (default de Promociones). */
export function codeIsSureKeepOnly(sessionId: string, code: string): boolean {
    return !(sessionPromotionState(sessionId).anyFinancierCodes ?? []).includes(code);
}
